import React, { useMemo, useState } from 'react';
import {
  IconButton,
  FlexRow,
  Text,
  FlexSpacer,
  FlexCell,
  DropMarker,
  VirtualList,
} from '@epam/promo';
import {
  DndActor,
  cx,
  DropParams,
  uuiDndState,
  getOrderBetween,
  DndActorRenderParams,
  VirtualListState,
} from '@epam/uui-core';
import { ReactComponent as trashIcon } from '@epam/assets/icons/common/action-delete-18.svg';
import { ReactComponent as editIcon } from '@epam/assets/icons/common/content-edit-18.svg';
import { DragHandle } from '@epam/uui-components';
import { sortBy } from 'lodash';
import { Tag } from 'components/common/Tag';
import {
  useModal,
  useTaskGroupsDeleteQuery,
  useTaskGroupsUpdateQuery,
} from 'hooks';
import {
  setDraftTaskGroup,
  setIsControlsShown,
  setSelectedTaskGroup,
  useTaskGroupsStore,
} from 'store/task-groups';
import { getFormatedText, isFixedGroup } from 'hooks/helpers/common';
import { TaskGroup } from 'models/task-groups.model';
import { DragableItemProps } from './TaskGroupsItem.model';
import css from './TaskGroupsItem.module.scss';

export const DragableGroupsItem = ({ groups }: DragableItemProps) => {
  const { mutate: updateTaskGroup } = useTaskGroupsUpdateQuery();
  const { mutate: deleteTaskGroup } = useTaskGroupsDeleteQuery();
  const { isControlsShown } = useTaskGroupsStore((state) => state);
  const sortedGroups = useMemo(() => sortBy(groups, ['order']), [groups]);

  const [state, setState] = useState<VirtualListState>({
    topIndex: 0,
    visibleCount: 10,
  });
  const modal = useModal();

  const handleDelete = (id: string) => {
    modal.delete({
      text: 'All tasks will be moved to "Other tasks" group. Do you want to continue?',
      highlights: ['"Other tasks"', 'continue'],
      onSuccess: () => deleteTaskGroup({ id }),
    });
  };

  const canAcceptDrop = () => ({
    top: true,
    bottom: true,
  });

  const handleOnDrop = (
    params: DropParams<TaskGroup, TaskGroup>,
    prevItem: TaskGroup,
    nextItem: TaskGroup,
  ) => {
    const { srcData, dstData, position } = params;

    const newOrder =
      position === 'bottom'
        ? getOrderBetween(dstData?.order ?? null, nextItem?.order ?? null)
        : getOrderBetween(prevItem?.order ?? null, dstData?.order ?? null);

    if (dstData?.name !== srcData.name) {
      updateTaskGroup({
        id: srcData.id,
        taskGroup: { ...srcData, order: newOrder },
      });
    }
  };

  const renderMaterial = (
    currentItem: TaskGroup,
    prevItem: TaskGroup,
    nextItem: TaskGroup,
  ) => (
    <DndActor
      key={currentItem.id}
      canAcceptDrop={canAcceptDrop}
      dstData={currentItem}
      render={(params: DndActorRenderParams) => (
        <div
          ref={params.ref}
          {...params.eventHandlers}
          className={cx(css.dragElement, css.container, params.classNames)}>
          <div
            className={cx(css.item, css.dndItem, {
              [uuiDndState.dragGhost]: params.isDragGhost,
            })}
            data-testid="group-list-item">
            <FlexRow columnGap="6" cx={css.header}>
              <FlexCell>
                <DragHandle cx={[css.dragHandle]} />
              </FlexCell>
              <FlexCell width="auto">
                <Tag
                  tooltip="Amount of tasks"
                  value={String(currentItem.tasks?.length)}
                />
              </FlexCell>
              <FlexCell width="auto">
                <Text
                  cx={`${css.text} ${css.title}`}
                  rawProps={{ 'data-testid': 'group-list-title' }}>
                  <b>{getFormatedText(currentItem.name)}</b>
                </Text>
                {currentItem.question && (
                  <Text
                    cx={css.text}
                    rawProps={{ 'data-testid': 'group-list-question' }}>
                    {getFormatedText(currentItem.question)}
                  </Text>
                )}
              </FlexCell>
              {isControlsShown && isFixedGroup(currentItem.name) && (
                <>
                  <FlexSpacer />
                  <IconButton
                    cx={css.icon}
                    icon={editIcon}
                    onClick={() => {
                      setSelectedTaskGroup(currentItem);
                      setDraftTaskGroup(currentItem);
                      setIsControlsShown(false);
                    }}
                  />
                  <IconButton
                    cx={css.icon}
                    icon={trashIcon}
                    onClick={() => handleDelete(currentItem.id)}
                  />
                </>
              )}
            </FlexRow>
            <DropMarker {...params} />
          </div>
        </div>
      )}
      srcData={currentItem}
      onDrop={(params) => handleOnDrop(params, prevItem, nextItem)}
    />
  );

  return (
    <VirtualList
      cx={css.list}
      role="listbox"
      rows={sortedGroups.map((group, index) =>
        renderMaterial(group, sortedGroups[index - 1], sortedGroups[index + 1]),
      )}
      rowsCount={groups.length}
      value={state}
      onValueChange={setState}
    />
  );
};
